// [UAT-PROFILE] — hydrate the CURRENT USER's profile photo for the session, in ONE place
// (sibling of useBrandLogoHydration, same pattern).
//
// Why: the persisted profile-photo value is a stable OCI storage KEY (V069 USER_ACCOUNT.
// PROFILE_PHOTO_KEY, e.g. tenants/<id>/assets/<user>/photo.png) which must be resolved into a
// short-lived presigned URL via POST /documents/access every session. The login paths write ""
// into localStorage PROFILE_PIC (the enterprise login response carries no photo), so the sidebar
// user chip / chat avatar stayed on the default image until the user visited Account Settings.
// Resolving here — on mount of the authenticated layout (Layouts.tsx) — covers every entry path:
// fresh password login, SSO callback, forced password update, and stale sessions whose stored
// presigned URL has expired.
//
// Cache contract: USER_PHOTO is the session cache of the resolved URL; PROFILE_PIC is kept in
// sync because the existing consumers (Sidebar chip, ChatSection getProfilePic) read it. "" means
// "no photo" — consumers fall back to the default avatar/initials gracefully.

import { useEffect } from "react";
import { useAppDispatch } from "../redux/hooks";
import { UPDATE_USER_PROFILE } from "../redux/action/ActionType";
import API from "../constants/API";
import { authHeader } from "../common/mainfunction";
import { GET_USER_BY_ID } from "../common/url";
import { getUserId } from "../common/function";
import { resolveAssetUrl } from "../services/storageAssetService";

export default function useUserPhotoHydration(): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only for authenticated sessions (Layouts is auth-gated, but be defensive).
      if (!localStorage.getItem("AUTH_TOKEN")) return;
      let resolvedUrl = "";
      try {
        const response = await API({
          method: "GET",
          url: GET_USER_BY_ID,
          headers: authHeader(),
          params: { userId: getUserId() },
        });
        const { status, data } = (response.data ?? {}) as {
          status?: string;
          data?: { profileLogo?: string | null }[];
        };
        if (status === "Success") {
          // Key -> fresh presigned URL ("" when no photo or on failure). Legacy absolute URLs
          // pass through unchanged inside resolveAssetUrl.
          resolvedUrl = await resolveAssetUrl(data?.[0]?.profileLogo ?? "");
        }
      } catch {
        // Non-fatal: fall through to "" (default avatar) below.
      }
      if (cancelled) return;
      localStorage.setItem("USER_PHOTO", resolvedUrl);
      localStorage.setItem("PROFILE_PIC", resolvedUrl);
      dispatch({
        type: UPDATE_USER_PROFILE,
        payload: resolvedUrl,
        userName: localStorage.getItem("LOGIN_NAME") ?? "",
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);
}
